'use client';

import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/cn';

/* ================================================================
   Delos Tooltip — 极简提示
   ================================================================ */

interface TooltipProps {
  content: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  children: React.ReactElement;
  className?: string;
}

const sideStyles = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const sideMotion = {
  top: { y: 4 },
  bottom: { y: -4 },
  left: { x: 4 },
  right: { x: -4 },
};

export function Tooltip({
  content,
  side = 'top',
  delay = 300,
  children,
  className,
}: TooltipProps) {
  const [show, setShow] = useState(false);
  const tooltipId = useId();
  const reduceMotion = useReducedMotion();
  const child = children as React.ReactElement<{ 'aria-describedby'?: string }>;
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleEnter = useCallback(() => {
    if (delay === 0) {
      setShow(true);
    } else {
      timer.current = setTimeout(() => setShow(true), delay);
    }
  }, [delay]);

  const handleLeave = useCallback(() => {
    clearTimeout(timer.current);
    setShow(false);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={() => setShow(true)}
      onBlur={handleLeave}
      onKeyDown={(event) => { if (event.key === 'Escape') handleLeave(); }}
    >
      {React.cloneElement(child, {
        'aria-describedby': show ? [child.props['aria-describedby'], tooltipId].filter(Boolean).join(' ') : child.props['aria-describedby'],
      })}
      <AnimatePresence>
        {show && (
          <div
            className={cn(
              'absolute z-[200] pointer-events-none',
              sideStyles[side],
            )}
          >
            <motion.div
              role="tooltip"
              id={tooltipId}
              initial={reduceMotion ? false : { opacity: 0, ...sideMotion[side] }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, ...sideMotion[side] }}
              transition={{ duration: reduceMotion ? 0 : 0.15 }}
              className={cn(
                'max-w-[min(280px,calc(100vw-32px))] rounded-xl bg-[#203644] px-3 py-2 text-xs font-medium whitespace-normal text-white shadow-lg',
                className,
              )}
            >
              {content}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
